import React, { useState } from 'react';
import { useEffect } from 'react';
import { BrowserRouter, Link, Outlet, Route, Routes, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import './index.css';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import Solutions from './pages/Solutions';
import Compliance from './pages/Compliance';
import Resources from './components/Resources';
import Contact from './components/Contact';
import Admin from './pages/Admin';
import UserPanel from './pages/UserPanel';
import CheckoutStatus from './pages/CheckoutStatus';
import AuthModal from './components/AuthModal';
import CartDrawer from './components/CartDrawer';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.querySelector(hash);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname, hash]);

  return null;
}

function Layout() {
  const location = useLocation();
  const navigate  = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const handleOrderPlaced = (orderId) => {
    navigate('/account');
  };

  return (
    <div className="App">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <Navbar
        onOpenAuth={() => setAuthOpen(true)}
        onOpenCart={() => setCartOpen(true)}
      />
      <main id="main-content" className="page-route" key={location.pathname}>
        <Outlet context={{ onRequireAuth: () => setAuthOpen(true) }} />
      </main>
      <Footer />

      {/* Global overlays */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
      />
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        onOrderPlaced={handleOrderPlaced}
        onRequireAuth={() => setAuthOpen(true)}
      />
    </div>
  );
}

function NotFound() {
  return (
    <section className="section not-found-page">
      <div className="container not-found-card">
        <p className="eyebrow">404</p>
        <h1>That page is not in the linen room.</h1>
        <p>
          The page may have moved, but the product catalog, compliance library,
          and quote request are ready.
        </p>
        <Link className="btn-primary" to="/">
          Return home <ArrowRight size={18} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

// ── Products page wrapper to receive onRequireAuth from Outlet context ──────────
function ProductsWithAuth() {
  const ctx = useOutletContext();
  return <Products onRequireAuth={ctx?.onRequireAuth} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ScrollManager />
          <Routes>
            {/* Admin is standalone — no Navbar or Footer */}
            <Route path="/admin" element={<Admin />} />
            <Route element={<Layout />}>
              <Route path="/"           element={<Home />} />
              <Route path="/products"   element={<ProductsWithAuth />} />
              <Route path="/solutions"  element={<Solutions />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/resources"  element={<Resources />} />
              <Route path="/contact"    element={<Contact />} />
              <Route path="/account"    element={<UserPanel />} />
              <Route path="/checkout/:status" element={<CheckoutStatus />} />
              <Route path="*"           element={<NotFound />} />
            </Route>
          </Routes>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
